import { useState, useEffect } from "react";
import { sendError } from "@/lib/client/sentry";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

/**
 * Hook for fetching related products for a given item.
 *
 * Fetches products similar to the given item ID in the specified currency.
 * Only fetches when both id and currency are provided.
 *
 * @param options - Configuration object
 * @param options.id - The item ID to find related products for
 * @param options.currency - Currency code for pricing (e.g., "USD", "EUR")
 * @returns Object containing:
 * - products: Array of related product items
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function RelatedProducts({ itemId, currency }) {
 *   const { products, loading } = useRelatedProducts({
 *     id: itemId,
 *     currency: "USD"
 *   });
 *
 *   if (loading) return <Skeleton count={4} />;
 *
 *   return (
 *     <div>
 *       {products.map(product => (
 *         <ProductCard key={product.id} {...product} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
const useRelatedProducts = ({ id, currency }: { id: number; currency: string }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRelated = async () => {
      setLoading(true);
      try {
        const data = await fetch(`${API_URL}/api/v2/search/related-products`, {
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            itemId: id,
            currency: currency,
          }),
        }).then((response) => response?.json());
        const response = data?.data;
        if (!!response?.items?.length) {
          setProducts(response?.items);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.log("Error fetching related products:", error);
        sendError(error as Error, { id, currency });
      }
      setLoading(false);
    };

    if (id && currency) {
      fetchRelated();
    }
  }, [id, currency]);

  return {
    products,
    loading,
  };
};

/**
 * Hook for fetching related products from the same vendor.
 *
 * Finds other products sold by the same vendor as the given item.
 *
 * @param options - Configuration object
 * @param options.id - The item ID to find vendor products for
 * @param options.currency - Currency code for pricing
 * @returns Object containing:
 * - vendorProducts: Array of products from the same vendor
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function VendorProducts({ itemId }) {
 *   const { vendorProducts, loading } = useRelatedProductsVendor({
 *     id: itemId,
 *     currency: "USD"
 *   });
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <Section title="More from this vendor">
 *       {vendorProducts.map(p => (
 *         <ProductCard key={p.id} {...p} />
 *       ))}
 *     </Section>
 *   );
 * }
 * ```
 */
const useRelatedProductsVendor = ({ id, currency }: { id: number; currency: string }) => {
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRelated = async () => {
      setLoading(true);
      try {
        const data = await fetch(`${API_URL}/api/v2/search/vendor-related-products`, {
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            itemId: id,
            currency: currency,
          }),
        }).then((response) => response?.json());

        const response = data?.data;
        if (!!response?.items?.length) {
          setVendorProducts(response?.items);
        } else {
          setVendorProducts([]);
        }
      } catch (error) {
        console.warn("Error fetching vendor related products:", error);
        sendError(error as Error, { id, currency });
      }

      setLoading(false);
    };

    if (id && currency) {
      fetchRelated();
    }
  }, [id, currency]);

  return {
    vendorProducts,
    loading,
  };
};

/**
 * Hook for fetching related products for slide-in panel display.
 *
 * Similar to useRelatedProducts but specifically for the slide-in panel.
 * Handles optional ID and currency parameters more gracefully.
 *
 * @param options - Configuration object
 * @param options.id - The item ID (optional)
 * @param options.currency - Currency code (optional)
 * @returns Object containing:
 * - slideinProducts: Array of related product items
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function SlideinRelated({ itemId }) {
 *   const { slideinProducts, loading } = useRelatedSlidein({
 *     id: itemId,
 *     currency: "USD"
 *   });
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <div className="related-grid">
 *       {slideinProducts.map(p => (
 *         <CompactProductCard key={p.id} {...p} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
const useRelatedSlidein = ({ id, currency }: { id?: number; currency?: string }) => {
  const [slideinProducts, setSlideinProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRelated = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/v2/search/slidein-related-products`, {
          credentials: "include",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            itemId: id,
            currency: currency,
          }),
        }).then((response) => response?.json());

        if (response?.data) {
          setSlideinProducts(response?.data || []);
        } else {
          setSlideinProducts([]);
        }
      } catch (error) {
        console.warn("Error fetching slide-in related products:", error);
        sendError(error as Error, { id, currency });
      }

      setLoading(false);
    };

    if (id) {
      fetchRelated();
    }
  }, [id, currency]);

  return {
    slideinProducts,
    loading,
  };
};

const useVisuallySimilar = ({ id, currency }: { id?: number; currency?: string }) => {
  const [visualProducts, setVisualProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    setVisualProducts([]);
    setLoading(true);

    const fetchSimilar = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v2/search/visually-similar-products`, {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ itemId: id, currency }),
          signal: controller.signal,
        }).then((r) => r?.json());

        setVisualProducts(response?.data || []);
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        console.warn("Error fetching visually similar products:", error);
        sendError(error as Error, { id, currency });
      }
      setLoading(false);
    };

    void fetchSimilar();
    return () => controller.abort();
  }, [id, currency]);

  return { visualProducts, loading };
};

export { useRelatedProducts, useRelatedProductsVendor, useRelatedSlidein, useVisuallySimilar };
